import { store, getElement } from '@wordpress/interactivity';
import './_animate.scss';
import {
  STORE_NAMESPACE,
  DEFAULT_SPEED,
  DEFAULT_ANIMATION,
  SPEED_MAP,
  CURSOR_CLASSES,
  OBSERVER_CONFIG,
  INITIAL_CYCLE_DELAY,
  DECODE_CHARACTERS,
} from './constants';

// Cancellable sleep helper using AbortSignal
function sleep(ms, signal) {
  return new Promise((resolve) => {
    const timer = setTimeout(finish, ms);

    function finish() {
      clearTimeout(timer);
      signal?.removeEventListener('abort', finish);
      resolve();
    }

    if (signal?.aborted) {
      finish();
      return;
    }

    signal?.addEventListener('abort', finish, { once: true });
  });
}

// Text String Creator
function createTextSetter(element) {
  const originalHTML = element.innerHTML;
  const originalText = element.textContent;

  // Check if there's any HTML formatting (not just plain text)
  const hasFormatting = originalHTML !== originalText;

  if (!hasFormatting) {
    // No formatting, just set text directly
    return (newText) => {
      element.textContent = newText;
    };
  }

  // Find all text nodes and their parent elements
  const textNodes = [];
  const walker = document.createTreeWalker(
    element,
    window.NodeFilter.SHOW_TEXT,
    null,
    false
  );
  let node;
  while ((node = walker.nextNode())) {
    if (node.textContent.trim()) {
      textNodes.push(node);
    }
  }

  if (textNodes.length === 0) {
    // Fallback to plain text if no text nodes found
    return (newText) => {
      element.textContent = newText;
    };
  }

  if (textNodes.length === 1) {
    // Single text node - simple case, just update it
    const textNode = textNodes[0];
    return (newText) => {
      textNode.textContent = newText;
    };
  }

  // Multiple text nodes (more complex case)
  const firstTextNode = textNodes[0];

  return (newText) => {
    // Clear all text nodes except the first
    for (let i = 1; i < textNodes.length; i++) {
      textNodes[i].textContent = '';
    }
    // Set the new text in the first text node
    firstTextNode.textContent = newText;
  };
}

// Typewriter cursor helpers
const cursorMap = new WeakMap();

function createCursor(element) {
  const cursor = document.createElement('span');
  cursor.className = CURSOR_CLASSES.base;
  cursor.setAttribute('aria-hidden', 'true');
  element.appendChild(cursor);
  cursorMap.set(element, cursor);
  return cursor;
}

function showCursor(element) {
  let cursor = cursorMap.get(element);

  // Check if cursor exists and is still in the DOM
  // (setText may have removed it via textContent assignment)
  if (cursor && !element.contains(cursor)) {
    // Cursor was removed from DOM, re-append it
    element.appendChild(cursor);
  } else if (!cursor) {
    // No cursor exists, create one
    cursor = createCursor(element);
  }

  cursor.classList.remove(CURSOR_CLASSES.hidden);
}

function removeCursor(element) {
  const cursor = cursorMap.get(element);
  if (cursor) {
    cursor.remove();
    cursorMap.delete(element);
  }
}

// Animation functions for transitioning IN
const transitionIn = {
  typewriter: async (element, text, speedMultiplier, setText) => {
    const speed = 50 * speedMultiplier;
    setText('');
    element.style.opacity = '1';
    element.style.transform = 'none';

    // Show blinking cursor while typing
    showCursor(element);

    for (let i = 0; i < text.length; i++) {
      setText(text.substring(0, i + 1));
      // Re-show cursor after setText (in case textContent removed it)
      showCursor(element);
      await new Promise((resolve) => setTimeout(resolve, speed));
    }
  },

  'fade-in': async (element, text, speedMultiplier, setText) => {
    const duration = 0.4 * speedMultiplier;

    // Step 1: Set initial state WITHOUT transition (invisible)
    element.style.transition = 'none';
    element.style.opacity = '0';
    setText(text);

    // Force reflow to ensure initial state is applied
    void element.offsetHeight;

    // Step 2: Enable transition and animate to visible state
    element.style.transition = `opacity ${duration}s ease-in-out`;

    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        element.style.opacity = '1';
        setTimeout(resolve, duration * 1000);
      });
    });
  },

  'slide-up': async (element, text, speedMultiplier, setText) => {
    const duration = 0.4 * speedMultiplier;

    // Step 1: Set initial state WITHOUT transition (invisible, below)
    element.style.transition = 'none';
    element.style.opacity = '0';
    element.style.transform = 'translateY(15px)';
    setText(text);

    // Force reflow to ensure initial state is applied
    void element.offsetHeight;

    // Step 2: Enable transition and animate to visible state
    element.style.transition = `opacity ${duration}s ease-out, transform ${duration}s ease-out`;

    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        element.style.opacity = '1';
        element.style.transform = 'translateY(0)';
        setTimeout(resolve, duration * 1000);
      });
    });
  },

  bounce: async (element, text, speedMultiplier, setText) => {
    const fadeDuration = 0.3 * speedMultiplier;
    const bounceDuration = 0.6 * speedMultiplier;

    // Step 1: Reset styles and set new text (invisible)
    element.style.animation = 'none';
    element.style.transform = '';
    element.style.transition = 'none';
    element.style.opacity = '0';
    setText(text);

    // Force reflow
    void element.offsetHeight;

    // Step 2: Fade in
    element.style.transition = `opacity ${fadeDuration}s ease-in-out`;
    element.style.opacity = '1';

    // Wait for fade to complete, plus a brief pause before bouncing
    await new Promise((resolve) =>
      setTimeout(resolve, fadeDuration * 1000 + 200)
    );

    // Step 3: Apply bounce
    element.style.transition = 'none';
    element.style.animation = 'none';
    void element.offsetHeight;
    element.style.animation = `amp-bounce ${bounceDuration}s ease`;

    await new Promise((resolve) => {
      setTimeout(() => {
        element.style.animation = 'none';
        resolve();
      }, bounceDuration * 1000);
    });
  },

  rotate: async (element, text, speedMultiplier, setText) => {
    const duration = 0.5 * speedMultiplier;

    // Step 1: Set initial state - rotated away (from below)
    element.style.transition = 'none';
    element.style.opacity = '0';
    element.style.transform = 'rotateX(-90deg)';
    element.style.transformOrigin = 'center bottom';
    setText(text);

    // Force reflow
    void element.offsetHeight;

    // Step 2: Rotate into view
    element.style.transition = `opacity ${duration}s ease-out, transform ${duration}s ease-out`;

    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        element.style.opacity = '1';
        element.style.transform = 'rotateX(0deg)';
        setTimeout(resolve, duration * 1000);
      });
    });
  },

  decode: async (element, text, speedMultiplier, setText) => {
    const chars = DECODE_CHARACTERS;
    const iterations = Math.ceil(8 / speedMultiplier);
    const stepDuration = 40 * speedMultiplier;

    element.style.opacity = '1';
    element.style.transform = 'none';

    // Create array to track which characters are revealed
    const revealed = new Array(text.length).fill(false);
    let revealedCount = 0;

    // Scramble phase - show random characters
    for (let i = 0; i < iterations; i++) {
      let display = '';
      for (let j = 0; j < text.length; j++) {
        if (revealed[j]) {
          display += text[j];
        } else if (text[j] === ' ') {
          display += ' ';
        } else {
          display += chars[Math.floor(Math.random() * chars.length)];
        }
      }
      setText(display);
      await new Promise((resolve) => setTimeout(resolve, stepDuration));
    }

    // Reveal phase - gradually reveal correct characters
    while (revealedCount < text.length) {
      // Find unrevealed non-space characters
      const unrevealed = [];
      for (let i = 0; i < text.length; i++) {
        if (!revealed[i] && text[i] !== ' ') {
          unrevealed.push(i);
        }
      }

      if (unrevealed.length === 0) break;

      // Reveal 1-2 random characters
      const toReveal = Math.min(
        Math.ceil(Math.random() * 2),
        unrevealed.length
      );
      for (let i = 0; i < toReveal; i++) {
        const idx = Math.floor(Math.random() * unrevealed.length);
        revealed[unrevealed[idx]] = true;
        unrevealed.splice(idx, 1);
        revealedCount++;
      }

      // Build display string
      let display = '';
      for (let j = 0; j < text.length; j++) {
        if (revealed[j] || text[j] === ' ') {
          display += text[j];
        } else {
          display += chars[Math.floor(Math.random() * chars.length)];
        }
      }
      setText(display);
      await new Promise((resolve) => setTimeout(resolve, stepDuration));
    }

    // Final - ensure correct text is shown
    setText(text);
  },
};

// Animation functions for transitioning OUT
const transitionOut = {
  typewriter: async (element, speedMultiplier, setText) => {
    const speed = 30 * speedMultiplier;

    // Show cursor while deleting
    showCursor(element);

    // Get text content excluding the cursor element
    let currentText = element.textContent.replace(/\u200B/g, ''); // Remove any zero-width spaces
    const cursor = cursorMap.get(element);
    if (cursor) {
      currentText = currentText.replace(cursor.textContent, '');
    }

    while (currentText.length > 0) {
      currentText = currentText.slice(0, -1);
      setText(currentText);
      // Re-show cursor after setText (in case textContent removed it)
      showCursor(element);
      await new Promise((resolve) => setTimeout(resolve, speed));
    }
  },

  'fade-in': async (element, speedMultiplier) => {
    const duration = 0.3 * speedMultiplier;
    element.style.transition = `opacity ${duration}s ease-in-out`;
    element.style.opacity = '0';
    await new Promise((resolve) => setTimeout(resolve, duration * 1000));
  },

  'slide-up': async (element, speedMultiplier) => {
    const duration = 0.3 * speedMultiplier;
    element.style.transition = `opacity ${duration}s ease-in, transform ${duration}s ease-in`;
    element.style.opacity = '0';
    element.style.transform = 'translateY(-15px)';
    await new Promise((resolve) => setTimeout(resolve, duration * 1000));
  },

  bounce: async (element, speedMultiplier) => {
    const duration = 0.3 * speedMultiplier;
    element.style.transition = `opacity ${duration}s ease-in-out`;
    element.style.opacity = '0';
    await new Promise((resolve) => setTimeout(resolve, duration * 1000));
  },

  rotate: async (element, speedMultiplier) => {
    const duration = 0.4 * speedMultiplier;
    element.style.transformOrigin = 'center top';
    element.style.transition = `opacity ${duration}s ease-in, transform ${duration}s ease-in`;
    element.style.opacity = '0';
    element.style.transform = 'rotateX(90deg)';
    await new Promise((resolve) => setTimeout(resolve, duration * 1000));
  },

  decode: async (element, speedMultiplier, setText) => {
    const chars = DECODE_CHARACTERS;
    const currentText = element.textContent;
    const iterations = Math.ceil(6 / speedMultiplier);
    const stepDuration = 30 * speedMultiplier;

    // Scramble the text progressively
    const scrambled = new Array(currentText.length).fill(false);
    let scrambledCount = 0;

    while (scrambledCount < currentText.length) {
      // Scramble 1-2 characters at a time
      const unscrambled = [];
      for (let i = 0; i < currentText.length; i++) {
        if (!scrambled[i] && currentText[i] !== ' ') {
          unscrambled.push(i);
        }
      }

      if (unscrambled.length === 0) break;

      const toScramble = Math.min(
        Math.ceil(Math.random() * 2),
        unscrambled.length
      );
      for (let i = 0; i < toScramble; i++) {
        const idx = Math.floor(Math.random() * unscrambled.length);
        scrambled[unscrambled[idx]] = true;
        unscrambled.splice(idx, 1);
        scrambledCount++;
      }

      let display = '';
      for (let j = 0; j < currentText.length; j++) {
        if (currentText[j] === ' ') {
          display += ' ';
        } else if (scrambled[j]) {
          display += chars[Math.floor(Math.random() * chars.length)];
        } else {
          display += currentText[j];
        }
      }
      setText(display);
      await new Promise((resolve) => setTimeout(resolve, stepDuration));
    }

    // Final scramble iterations (no fade - transition directly to next decode)
    for (let i = 0; i < iterations; i++) {
      let display = '';
      for (let j = 0; j < currentText.length; j++) {
        if (currentText[j] === ' ') {
          display += ' ';
        } else {
          display += chars[Math.floor(Math.random() * chars.length)];
        }
      }
      setText(display);
      await new Promise((resolve) => setTimeout(resolve, stepDuration));
    }
  },
};

// Helper to trigger initial bounce animation
async function triggerBounce(element, speedMultiplier) {
  const bounceDuration = 0.6 * speedMultiplier;

  element.style.animation = 'none';
  element.style.transition = 'none';
  void element.offsetHeight;
  element.style.animation = `amp-bounce ${bounceDuration}s ease`;

  await new Promise((resolve) => {
    setTimeout(() => {
      element.style.animation = 'none';
      resolve();
    }, bounceDuration * 1000);
  });
}

// Store active animation cycles to prevent duplicates
const activeAnimations = new WeakSet();

// Helper to trigger a one-time entrance animation
async function triggerEntranceAnimation(
  element,
  animation,
  text,
  speedMultiplier,
  setText
) {
  element.style.display = 'inline-block';

  switch (animation) {
    case 'bounce':
      await triggerBounce(element, speedMultiplier);
      break;
    case 'fade-in':
      // Quick fade in effect
      element.style.transition = 'none';
      element.style.opacity = '0';
      void element.offsetHeight;
      element.style.transition = `opacity ${0.4 * speedMultiplier}s ease-in-out`;
      element.style.opacity = '1';
      await new Promise((resolve) =>
        setTimeout(resolve, 0.4 * speedMultiplier * 1000)
      );
      break;
    case 'slide-up':
      element.style.transition = 'none';
      element.style.opacity = '0';
      element.style.transform = 'translateY(15px)';
      void element.offsetHeight;
      element.style.transition = `opacity ${0.4 * speedMultiplier}s ease-out, transform ${0.4 * speedMultiplier}s ease-out`;
      element.style.opacity = '1';
      element.style.transform = 'translateY(0)';
      await new Promise((resolve) =>
        setTimeout(resolve, 0.4 * speedMultiplier * 1000)
      );
      break;
    case 'rotate':
      element.style.transition = 'none';
      element.style.opacity = '0';
      element.style.transform = 'rotateX(-90deg)';
      element.style.transformOrigin = 'center bottom';
      void element.offsetHeight;
      element.style.transition = `opacity ${0.5 * speedMultiplier}s ease-out, transform ${0.5 * speedMultiplier}s ease-out`;
      element.style.opacity = '1';
      element.style.transform = 'rotateX(0deg)';
      await new Promise((resolve) =>
        setTimeout(resolve, 0.5 * speedMultiplier * 1000)
      );
      break;
    case 'decode':
      // Run the decode animation on the existing text
      await transitionIn.decode(element, text, speedMultiplier, setText);
      break;
    case 'typewriter':
      // Re-type the existing text
      const initialHeight = element.offsetHeight;
      element.style.minHeight = `${initialHeight}px`;
      setText('');
      element.style.opacity = '1';

      // Show blinking cursor while typing
      showCursor(element);

      const speed = 50 * speedMultiplier;
      for (let i = 0; i < text.length; i++) {
        setText(text.substring(0, i + 1));
        // Re-show cursor after setText (in case textContent removed it)
        showCursor(element);
        await new Promise((resolve) => setTimeout(resolve, speed));
      }
      break;
  }
}

// Start word cycling for an element
async function startWordCycle(element, signal) {
  if (signal?.aborted || !element.isConnected) {
    return;
  }

  // Prevent duplicate animations
  if (activeAnimations.has(element)) {
    return;
  }
  activeAnimations.add(element);

  const animation = element.dataset.animation || DEFAULT_ANIMATION;
  element.style.display = 'inline-block';

  // For typewriter animation, capture initial height and show cursor immediately
  if (animation === 'typewriter') {
    const initialHeight = element.offsetHeight;
    element.style.minHeight = `${initialHeight}px`;
    // Show cursor right away so it's visible on page load
    showCursor(element);
  }

  // Initial delay before starting animations
  await sleep(INITIAL_CYCLE_DELAY, signal);
  if (signal?.aborted || !element.isConnected) {
    activeAnimations.delete(element);
    return;
  }

  const speed = element.dataset.speed || DEFAULT_SPEED;
  const words = element.dataset.words || '';
  const originalText = element.textContent;
  const speedSettings = SPEED_MAP[speed] || SPEED_MAP[DEFAULT_SPEED];

  // Create a text setter that preserves any inner HTML formatting (bold, italic, etc.)
  const setText = createTextSetter(element);

  // Parse words list
  const wordList = words
    ? [originalText, ...words.split(',').map((w) => w.trim())]
    : [originalText];

  // If only one word, trigger entrance animation and stop (no cycling needed)
  if (wordList.length <= 1) {
    await triggerEntranceAnimation(
      element,
      animation,
      originalText,
      speedSettings.multiplier,
      setText
    );
    return;
  }

  // For multiple words, skip the initial entrance animation since the first word is already visible
  // Start cycling: wait → transition out → transition in next word → repeat
  let currentIndex = 0;

  const cycle = async () => {
    if (signal?.aborted || !element.isConnected) {
      activeAnimations.delete(element);
      return;
    }

    // Wait before next transition
    await sleep(speedSettings.interval, signal);

    if (signal?.aborted || !element.isConnected) {
      activeAnimations.delete(element);
      return;
    }

    // Transition out current word
    if (transitionOut[animation]) {
      await transitionOut[animation](
        element,
        speedSettings.multiplier,
        setText
      );
    }

    if (signal?.aborted || !element.isConnected) {
      activeAnimations.delete(element);
      return;
    }

    // Move to next word
    currentIndex = (currentIndex + 1) % wordList.length;

    // Transition in with new word (pass setText to preserve formatting)
    if (transitionIn[animation]) {
      await transitionIn[animation](
        element,
        wordList[currentIndex],
        speedSettings.multiplier,
        setText
      );
    }

    if (signal?.aborted || !element.isConnected) {
      activeAnimations.delete(element);
      return;
    }

    // Continue cycling
    cycle();
  };

  cycle();
}

// Shared Intersection Observer for scroll-triggered animations
const signalMap = new WeakMap();
let sharedObserver;

function getSharedObserver() {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          sharedObserver.unobserve(entry.target);
          const signal = signalMap.get(entry.target);
          signalMap.delete(entry.target);

          if (!signal?.aborted) {
            startWordCycle(entry.target, signal);
          }
        }
      });
    }, OBSERVER_CONFIG);
  }
  return sharedObserver;
}

function setupIntersectionObserver(element, signal) {
  if (signal?.aborted) {
    return;
  }

  if (typeof IntersectionObserver === 'undefined') {
    startWordCycle(element, signal);
    return;
  }

  signalMap.set(element, signal);
  getSharedObserver().observe(element);
}

// Register the Interactivity API store
store(STORE_NAMESPACE, {
  callbacks: {
    init: () => {
      const { ref: element } = getElement();
      const controller = new AbortController();

      // Setup intersection observer to start animation when visible
      setupIntersectionObserver(element, controller.signal);

      // Cleanup function executed when the element unmounts from the DOM
      return () => {
        controller.abort();
        sharedObserver?.unobserve(element);
        signalMap.delete(element);
        removeCursor(element);
        activeAnimations.delete(element);
      };
    },
  },
});
